import React, { useEffect, useMemo, useState } from "react";
import "./SubjectCategorySlider.css";
import MultipleItemSlider from "./components/MultipleItemSlider";
import { useSetDefaultSubjectCategory } from "./hooks/useSetDefaultSubjectCategory";

const subjectSliderSettings = {
  dots: false,
  infinite: false,
  speed: 500,
  slidesToShow: 5,
  slidesToScroll: 1,
  autoplay: false,
  arrows: true,

  swipeToSlide: true,
  focusOnSelect: false,
  touchMove: true,
  touchThreshold: 15,

  responsive: [
    {
      breakpoint: 1600,
      settings: {
        slidesToShow: 4.5,
      },
    },
    {
      breakpoint: 1400,
      settings: {
        slidesToShow: 4,
      },
    },
    {
      breakpoint: 1200,
      settings: {
        slidesToShow: 3.5,
      },
    },
    {
      breakpoint: 992,
      settings: {
        slidesToShow: 3,
      },
    },
    {
      breakpoint: 768,
      settings: {
        slidesToShow: 2.2,
      },
    },
    {
      breakpoint: 576,
      settings: {
        slidesToShow: 1.7,
      },
    },
    {
      breakpoint: 400,
      settings: {
        slidesToShow: 1.25,
      },
    },
  ],
};

const SubjectIcon = () => {
  return (
    <span className="subject-card__icon" aria-hidden="true">
      <span className="subject-card__triangle" />

      <span className="subject-card__shapes">
        <span className="subject-card__circle" />
        <span className="subject-card__square" />
      </span>
    </span>
  );
};

const SubjectCard = ({
  item,
  isActive,
  onSelect,
  isSaving,
}) => {
  return (
    <div className="subject-slider__item">
      <button
        type="button"
        className={`subject-card ${
          isActive
            ? "subject-card--active"
            : ""
        }`}
        disabled={isSaving}
        aria-label={`Select ${item.title}`}
        aria-pressed={isActive}
        onClick={() => onSelect(item.id)}
      >
        {item.pinned && (
          <span
            className="subject-card__pin"
            aria-label="Pinned subject"
          >
           <i className="bi bi-patch-check-fill"></i>
          </span>
        )}

        <SubjectIcon />

        <span className="subject-card__content">
          <span className="subject-card__title">
            {item.title}
          </span>

          <span className="subject-card__subjects">
            {item.subjects} Subjects
          </span>
        </span>
      </button>
    </div>
  );
};

const SubjectSlider = ({ categories = [], allSubjectCount = 0, activeSubject, onSelect, queryParams = {}, persistSelection = true, onPersistSelection }) => {
  const [saveError, setSaveError] = useState("");
  const setDefaultSubjectCategoryMutation = useSetDefaultSubjectCategory(queryParams);
  const isSaving = setDefaultSubjectCategoryMutation.isPending;
  const sliderItems = useMemo(() => {
    const allItem = {
      id: "all",
      title: "All",
      subjects: allSubjectCount,
      pinned: false,
    };

    const categoryItems = categories.map((item) => ({
      id: Number(item.subject_category_id),
      title: item.name || "Subject Category",
      subjects: Number(item.subjects_count || 0),
      pinned: Number(item.is_default) === 1,
    }));

    return [allItem, ...categoryItems];
  }, [allSubjectCount, categories]);

  useEffect(() => {
    setSaveError("");
  }, [activeSubject]);

  const handleSubjectSelect = async (subjectId) => {
    onSelect(subjectId);

    if (subjectId === "all" || isSaving || !persistSelection) {
      if (!persistSelection && subjectId !== "all") {
        onPersistSelection?.(Number(subjectId));
      }
      return;
    }

    setSaveError("");

    try {
      await setDefaultSubjectCategoryMutation.mutateAsync({
        subject_category_id: Number(subjectId),
      });
    } catch (error) {
      setSaveError(error.message || "Unable to update the selected category.");
    }
  };

  return (
    <section
      className="subject-section"
      aria-label="subject categories"
    >
      {sliderItems.length ? (
        <MultipleItemSlider
          items={sliderItems}
          settings={subjectSliderSettings}
          className="subject-slider"
          renderItem={(item) => (
            <SubjectCard
              item={item}
              isActive={activeSubject === item.id}
              onSelect={handleSubjectSelect}
              isSaving={isSaving}
            />
          )}
        />
      ) : null}
      {isSaving ? <div className="cs-saveState">Updating selected category...</div> : null}
      {!isSaving && saveError ? <div className="cs-saveState">{saveError}</div> : null}
    </section>
  );
};

export default SubjectSlider;
